import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { BillingAgent, BillingAgentCreate, BillingAgentUpdate, Provider } from '../types';
import { validatePassword } from '../utils/fieldValidation';

const BillingAgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<BillingAgent[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<BillingAgent | null>(null);
  const [formData, setFormData] = useState<BillingAgentCreate>({
    first_name: '',
    last_name: '',
    phone: '',
    user_email: '',
    password: '',
    is_active: true,
    provider_ids: [],
  });
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([]);
  const [auditLog, setAuditLog] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [agentData, providerData] = await Promise.all([
        api.billingAgents.getAll(),
        api.providers.getAll(),
      ]);
      setAgents(agentData);
      setProviders(providerData);
    } catch (err) {
      console.error(err);
      setError('Failed to load billing agents.');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setIsEditMode(false);
    setSelectedAgent(null);
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      user_email: '',
      password: '',
      is_active: true,
      provider_ids: [],
    });
    setSelectedProviderIds([]);
    setAuditLog([]);
    setIsDialogOpen(true);
  };

  const openEdit = async (agent: BillingAgent) => {
    setIsEditMode(true);
    setSelectedAgent(agent);
    setFormData({
      first_name: agent.first_name,
      last_name: agent.last_name,
      phone: agent.phone || '',
      user_email: agent.user_email,
      password: '',
      is_active: agent.is_active,
      provider_ids: agent.provider_ids,
    });
    setSelectedProviderIds(agent.provider_ids);
    setIsDialogOpen(true);
    try {
      const audit = await api.billingAgents.getAuditLog(agent.billing_agent_id);
      setAuditLog(audit);
    } catch {
      setAuditLog([]);
    }
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      setError(null);
      if (!isEditMode) {
        if (!formData.password) {
          setError('Password is required.');
          return;
        }
        const pwdError = validatePassword(formData.password);
        if (pwdError) {
          setError(pwdError);
          return;
        }
      }

      if (isEditMode && selectedAgent) {
        const update: BillingAgentUpdate = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone || undefined,
          user_email: formData.user_email,
          is_active: formData.is_active,
        };
        if (formData.password) {
          update.password = formData.password;
        }
        await api.billingAgents.update(selectedAgent.billing_agent_id, update);
        await api.billingAgents.setProviders(selectedAgent.billing_agent_id, selectedProviderIds);
      } else {
        await api.billingAgents.create({
          ...formData,
          provider_ids: selectedProviderIds,
        });
      }
      setIsDialogOpen(false);
      await fetchData();
    } catch (err) {
      console.error(err);
      setError('Failed to save billing agent.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async (agent: BillingAgent) => {
    if (!window.confirm(`Deactivate billing agent ${agent.user_email}?`)) return;
    try {
      await api.billingAgents.delete(agent.billing_agent_id);
      await fetchData();
    } catch (err) {
      console.error(err);
      setError('Failed to deactivate billing agent.');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Billing Agents</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Billing Agent
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Providers</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.map((agent) => (
              <TableRow key={agent.billing_agent_id}>
                <TableCell>{agent.first_name} {agent.last_name}</TableCell>
                <TableCell>{agent.user_email}</TableCell>
                <TableCell>{agent.phone || '—'}</TableCell>
                <TableCell>{agent.provider_ids.length}</TableCell>
                <TableCell>
                  <Chip
                    label={agent.is_active ? 'Active' : 'Inactive'}
                    color={agent.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(agent)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(agent)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {agents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No billing agents found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Billing Agent' : 'Create Billing Agent'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.user_email}
                onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isEditMode ? 'New Password (optional)' : 'Password'}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Providers</InputLabel>
                <Select
                  multiple
                  value={selectedProviderIds}
                  onChange={(e) => setSelectedProviderIds(e.target.value as number[])}
                  input={<OutlinedInput label="Assigned Providers" />}
                  renderValue={(selected) =>
                    providers
                      .filter((p) => selected.includes(Number(p.provider_id)))
                      .map((p) => `${p.first_name} ${p.last_name}`)
                      .join(', ')
                  }
                >
                  {providers.map((provider) => {
                    const providerId = Number(provider.provider_id);
                    return (
                    <MenuItem key={providerId} value={providerId}>
                      <Checkbox checked={selectedProviderIds.includes(providerId)} />
                      <ListItemText primary={`${provider.first_name} ${provider.last_name}`} />
                    </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {isEditMode && auditLog.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Provider Assignment History</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>Provider ID</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLog.map((entry) => (
                      <TableRow key={entry.audit_id}>
                        <TableCell>{entry.action}</TableCell>
                        <TableCell>{entry.provider_id}</TableCell>
                        <TableCell>{new Date(entry.performed_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingAgentsPage;
